import React, { useContext, useState } from "react";
import axios from "axios";
import MessageBox from "./MessageBox";
import { UserContext } from "../../../Context/UserContext";

const InputField = ({ 
  type, 
  placeholder, 
  value, 
  onChange, 
  icon, 
  focused, 
  onFocus, 
  onBlur,
  showToggle = false,
  onToggle = null,
  showStrength = false,
  passwordStrength = 0,
  getPasswordStrengthColor,
  getPasswordStrengthText
}) => (
  <div className="relative group">
    <div className="relative">
      <div className={`absolute left-3 top-1/2 transform -translate-y-1/2 transition-all duration-200 z-10 ${
        focused || value ? 'text-[#10c9c0]' : 'text-gray-400 group-hover:text-gray-600'
      }`}>
        {icon}
      </div>
      
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        className={`w-full pl-10 pr-${showToggle ? '10' : '4'} py-3 border-2 rounded-xl bg-gray-50/50 transition-all duration-200 placeholder-gray-400 focus:placeholder-gray-300 ${
          focused || value
            ? 'border-[#10c9c0] bg-white shadow-lg shadow-[#10c9c0]/10 focus:outline-none'
            : 'border-gray-200 hover:border-gray-300 focus:border-[#10c9c0] focus:bg-white focus:outline-none focus:shadow-lg focus:shadow-[#10c9c0]/10'
        }`}
        required
      />
      
      {showToggle && (
        <button
          type="button"
          onClick={onToggle}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200 z-10"
        >
          {type === 'password' ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
            </svg>
          )}
        </button>
      )}
    </div>

    {/* Password Strength Indicator */}
    {showStrength && value && (
      <div className="mt-2 space-y-1">
        <div className="flex justify-between items-center text-xs">
          <span className="text-gray-500">Password strength</span>
          <span className={`font-medium ${
            passwordStrength <= 25 ? 'text-red-500' :
            passwordStrength <= 50 ? 'text-orange-500' :
            passwordStrength <= 75 ? 'text-yellow-500' : 'text-green-500'
          }`}>
            {getPasswordStrengthText()}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
          <div 
            className={`h-full transition-all duration-300 ${getPasswordStrengthColor()}`}
            style={{ width: `${passwordStrength}%` }}
          />
        </div>
      </div>
    )}
  </div>
);

const RegisterForm = ({ onToggle }) => {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [usernameFocused, setUsernameFocused] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const { setIsToken, setShowModal } = useContext(UserContext);

  // Password strength calculator
  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (/[a-z]/.test(password)) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password)) strength += 25;
    return strength;
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setPasswordStrength(calculatePasswordStrength(newPassword));
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength <= 25) return 'bg-red-500';
    if (passwordStrength <= 50) return 'bg-orange-500';
    if (passwordStrength <= 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength <= 25) return 'Weak';
    if (passwordStrength <= 50) return 'Fair';
    if (passwordStrength <= 75) return 'Good';
    return 'Strong';
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage("");

    if (username && email && password) {
      try {
        const response = await axios.post(
          "https://app.akiya2.com/register",
          {
            email: email,
            password: password,
            name: username,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        
        setMessage("Registration successful!");
        setMessageType("success");

        if (response.data.access_token) {
          localStorage.setItem("access_token", response.data.access_token);
          setShowModal(false);
          setIsToken(true);
        }
      } catch (error) {
        setMessage(error.response?.data?.detail || "Registration failed. Please try again.");
        setMessageType("error");
        console.error("Error:", error);
      }
    } else {
      setMessage("All fields are required");
      setMessageType("error");
    }
    
    setIsLoading(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#10c9c0] to-[#0eb1a9] rounded-2xl mb-4">
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Create Account</h2>
        <p className="text-gray-600">Join us today and get started</p>
      </div>

      {/* Message Box */}
      {message && (
        <div className="animate-in fade-in slide-in-from-top-2 duration-300">
          <MessageBox message={message} type={messageType} />
        </div>
      )}

      {/* Registration Form */}
      <form onSubmit={handleRegister} className="space-y-4">
        <InputField
          type="text"
          placeholder="Enter your username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          focused={usernameFocused}
          onFocus={() => setUsernameFocused(true)}
          onBlur={() => setUsernameFocused(false)}
          icon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          }
        />

        <InputField
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          focused={emailFocused}
          onFocus={() => setEmailFocused(true)}
          onBlur={() => setEmailFocused(false)}
          icon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
            </svg>
          }
        />

        <InputField
          type={showPassword ? "text" : "password"}
          placeholder="Create a strong password"
          value={password}
          onChange={handlePasswordChange}
          focused={passwordFocused}
          onFocus={() => setPasswordFocused(true)}
          onBlur={() => setPasswordFocused(false)}
          showToggle={true}
          showStrength={true}
          passwordStrength={passwordStrength}
          getPasswordStrengthColor={getPasswordStrengthColor}
          getPasswordStrengthText={getPasswordStrengthText}
          onToggle={() => setShowPassword(!showPassword)}
          icon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          }
        />

        {/* Terms and Privacy Notice */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
          <p className="text-xs text-gray-600 text-center">
            By creating an account, you agree to our{' '}
            <button type="button" className="text-[#10c9c0] hover:underline font-medium">
              Terms of Service
            </button>{' '}
            and{' '}
            <button type="button" className="text-[#10c9c0] hover:underline font-medium">
              Privacy Policy
            </button>
          </p>
        </div>

        {/* Register Button */}
        <button
          type="submit"
          disabled={isLoading}
          className={`w-full py-3 px-4 rounded-xl font-medium text-white transition-all duration-200 ${
            isLoading
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-gradient-to-r from-[#10c9c0] to-[#0eb1a9] hover:from-[#0eb1a9] hover:to-[#10c9c0] hover:scale-[1.02] hover:shadow-lg hover:shadow-[#10c9c0]/25 active:scale-[0.98]'
          }`}
        >
          {isLoading ? (
            <div className="flex items-center justify-center space-x-2">
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <span>Creating account...</span>
            </div>
          ) : (
            <div className="flex items-center justify-center space-x-2">
              <span>Create Account</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </div>
          )}
        </button>
      </form>

      {/* Footer */}
      <div className="space-y-4">
        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-white text-gray-500">Already have an account?</span>
          </div>
        </div>

        {/* Sign In Link */}
        <div className="text-center">
          <button 
            onClick={onToggle} 
            className="inline-flex items-center px-6 py-2 border-2 border-[#10c9c0] text-[#10c9c0] rounded-xl font-medium hover:bg-[#10c9c0] hover:text-white transition-all duration-200 hover:scale-105 active:scale-95"
          >
            <svg className="mr-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
            </svg>
            Sign In Instead
          </button>
        </div>
      </div>
    </div>
  );
};

export default RegisterForm;